































































































































































import Vue from "vue";
import { computed } from "@vue/composition-api";
import { SIZE, TYPE, useGenAiPalette } from "../data/design";

export interface LevelStop {
  /** short label under the track, e.g. "+ Medium" */
  label: string;
  /** full label in the status pill, e.g. "High + Medium risk" */
  pill: string;
  /** key into the palette's severity tones */
  key: string;
}

/**
 * The severity / sensitivity filter from Ami's 23-Aug design: a 1..N stepper
 * whose stops are visible as nodes on the track, with the widest included
 * level named in a pill and reachable by clicking its label.
 */
export default Vue.extend({
  props: {
    value: { type: Number, required: true },
    /** optional; without it the status pill sits alone on the right, as the
     *  gen-ai-ui rulers have it */
    heading: { type: String, default: "" },
    /** ordered stops, widest last */
    stops: { type: Array, required: true },
    /** optional glyph left of the heading — a sprite symbol id */
    icon: { type: String, default: "" },
    /**
     * Which tone map the stops read from. Risk cards use "severity" (warm),
     * data-classification cards use "sensitivity" (cool) — the two scales are
     * deliberately different colour languages, so the slider must not assume.
     */
    scale: { type: String, default: "severity" },
    aria_label: { type: String, default: "Filter level" },
    /** the lighter, tighter panel the matrix card uses (gen-ai-ui `compact`) */
    compact: { type: Boolean, default: false },
  },
  setup(props) {
    const palette = useGenAiPalette();
    const stop_list = computed(() => props.stops as LevelStop[]);

    const tones = computed(
      () =>
        (palette.value as any)[props.scale as string] || palette.value.severity
    );

    const stop_color = (n: number) =>
      tones.value[stop_list.value[n - 1].key].dot;

    const stop_text = (n: number) =>
      tones.value[stop_list.value[n - 1].key].chipText;

    /**
     * The fill reaches exactly the current node. Expressed as a scale factor
     * rather than a width: transform animates on the compositor, while
     * animating width relayouts the track on every frame.
     */
    const fill_ratio = computed(() => {
      const total = stop_list.value.length;
      return total > 1 ? (Number(props.value) - 1) / (total - 1) : 1;
    });

    /** the whole scale's gradient, squeezed into however much of the track
     *  is filled — gen-ai-ui draws it this way at every stop */
    const fill_gradient = computed(
      () =>
        (palette.value.sliderFill as Record<string, string>)[props.scale as string] ||
        palette.value.sliderFill.severity
    );

    /** the current stop sits in a wider ring and lifts (ring-4 + shadow-md);
     *  the others keep a thin ring so they read against the fill */
    const node_shadow = (current: boolean) =>
      current
        ? `0 0 0 4px ${palette.value.card.bg}, 0 4px 6px -1px rgba(0,0,0,.1), 0 2px 4px -2px rgba(0,0,0,.1)`
        : `0 0 0 2px ${palette.value.card.bg}`;

    return {
      stop_color,
      stop_text,
      fill_ratio,
      fill_gradient,
      node_shadow,
      T: computed(() => ({
        ...palette.value,
        // the heading glyph follows the card's own scale
        scale_tones: tones.value,
      })),
      TY: TYPE,
      S: SIZE,
    };
  },
});
